import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

const initialForm = {
  name: "",
  slug: "",
  industry: "",
  business_type: "",
  business_email: "",
  business_website: "",
  country_code: "IN",
  business_phone: "",
  owner_name: "",
  owner_email: "",
  owner_phone: "+91",
  country: "India",
  timezone: "Asia/Kolkata",
  plan: "starter",
  password: "",
  confirmPassword: "",
  metadata_json: "{}",
};

const fieldGroups = [
  ["name", "slug"],
  ["industry", "business_type"],
  ["business_email", "business_website"],
  ["country_code", "business_phone"],
  ["owner_name", "owner_email"],
  ["owner_phone", "country"],
  // ["timezone", "plan"],
  ["plan"],
];

const labels = {
  name: "Tenant name",
  slug: "Slug",
  industry: "Industry",
  business_type: "Business type",
  business_email: "Business email",
  business_website: "Business website",
  country_code: "Country code",
  business_phone: "Business phone number",
  owner_name: "Owner name",
  owner_email: "Owner email",
  owner_phone: "Owner phone",
  country: "Country",
  timezone: "Timezone",
  plan: "Plan",
  password: "Password",
  confirmPassword: "Confirm password",
  metadata_json: "Metadata JSON",
};

const isRequired = (field) => field !== "metadata_json";
const renderLabel = (field) => `${labels[field]}${isRequired(field) ? " *" : ""}`;

const validateField = (field, form) => {
  const val = (form[field] || "").trim();
  if (isRequired(field) && !val) return `${labels[field]} is required.`;
  if (field === "name" && !/^[a-zA-Z\s]+$/.test(val)) return "Name must only contain letters and spaces.";
  if (field === "name" && val.length < 3) return "Name must be at least 3 characters.";
  if (field === "slug" && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(val)) return "Slug must be lowercase letters, numbers, and hyphens only.";
  if (field === "owner_email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Enter a valid email address.";
  return "";
};

const validatePassword = (password) => {
  const checks = [];
  if (password.length < 8) checks.push("At least 8 characters");
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password))
    checks.push("One special character");
  if (!/\d/.test(password)) checks.push("One number");
  return checks;
};

const passFeedback = (pass) => {
  const r = [];
  r.push({ label: "At least 8 characters", ok: pass.length >= 8 });
  r.push({ label: "One special character", ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass) });
  r.push({ label: "One number", ok: /\d/.test(pass) });
  return r;
};

const TenantCreateForm = ({ onSubmit, loading, errorMessage }) => {
  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const phoneDigits = form.owner_phone.replace("+91", "");

  const errors = {};
  for (const field of ["name", "slug", "industry", "business_type", "owner_name", "owner_email"]) {
    errors[field] = touched[field] ? validateField(field, form) : "";
  }
  if (touched.owner_phone && phoneDigits.length > 0 && phoneDigits.length !== 10) {
    errors.owner_phone = "Phone must be exactly 10 digits after +91.";
  }
  if (touched.password && form.password) {
    const pw = validatePassword(form.password);
    if (pw.length > 0) errors.password = "Password must have: " + pw.join(", ") + ".";
  }
  if (touched.confirmPassword && form.confirmPassword && form.password !== form.confirmPassword) {
    errors.confirmPassword = "Passwords do not match.";
  }

  const touch = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const limited = digits.slice(0, 10);
    setForm((current) => ({ ...current, owner_phone: "+91" + limited }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const passwordError = validatePassword(form.password);
    if (passwordError.length > 0) {
      setServerError("Password must have: " + passwordError.join(", ") + ".");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setServerError("Passwords do not match.");
      return;
    }

    const trimmedTenantName = form.name.trim();
    const trimmedBusinessEmail = form.business_email.trim();
    const trimmedBusinessWebsite = form.business_website.trim();
    const trimmedCountryCode = form.country_code.trim().toUpperCase();
    const trimmedBusinessPhone = form.business_phone.trim();

    if (!trimmedTenantName || !trimmedBusinessEmail || !trimmedBusinessWebsite || !trimmedCountryCode || !trimmedBusinessPhone) {
      setServerError("WhatsApp onboarding fields are required: tenant name, business email, business website, country code, and business phone number.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedBusinessEmail)) {
      setServerError("Business email must be a valid email address.");
      return;
    }

    if (!/^https?:\/\/.+\..+/.test(trimmedBusinessWebsite)) {
      setServerError("Business website must be a valid URL starting with http:// or https://.");
      return;
    }

    if (!/^[A-Z]{2}$/.test(trimmedCountryCode)) {
      setServerError("Country code must be a two-letter ISO code like IN or US.");
      return;
    }

    if (!/^\+[1-9]\d{7,14}$/.test(trimmedBusinessPhone)) {
      setServerError("Business phone number must be in E.164 format, for example +919876543210.");
      return;
    }

   

    let metadataJson = {};
    if (form.metadata_json.trim()) {
      try {
        metadataJson = JSON.parse(form.metadata_json);
      } catch (error) {
        setServerError("Metadata JSON must be valid JSON.");
        return;
      }
    }

    const payload = {
      name: trimmedTenantName,
      slug: form.slug,
      industry: form.industry,
      business_type: form.business_type,
      business_email: trimmedBusinessEmail,
      business_website: trimmedBusinessWebsite,
      country_code: trimmedCountryCode,
      business_phone: trimmedBusinessPhone,
      owner_name: form.owner_name,
      owner_email: form.owner_email,
      owner_phone: form.owner_phone,
      country: form.country,
      timezone: form.timezone,
      plan: form.plan,
      owner_password: form.password,
      metadata_json: metadataJson,
    };

    await onSubmit(payload);
  };

  const feedback = passFeedback(form.password);
  const fieldStyle = (field) =>
    errors[field]
      ? "w-full rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-rose-400 focus:bg-white"
      : "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#096dea] focus:bg-white";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="grid gap-4 md:grid-cols-2">
        {fieldGroups.map((group) =>
          group.map((field) => {
            if (field === "owner_phone") {
              return (
                <label key={field} className="space-y-2" onFocus={() => touch("owner_phone")}>
                  <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-900">+91</span>
                    <input
                      type="text"
                      value={phoneDigits}
                      onChange={handlePhoneChange}
                      placeholder="9876543210"
                      className={`w-full rounded-xl border pl-12 pr-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
                        errors.owner_phone ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:bg-white" : "border-slate-200 bg-slate-50 focus:border-[#096dea] focus:bg-white"
                      }`}
                    />
                  </div>
                  {errors.owner_phone && <p className="text-xs text-rose-600">{errors.owner_phone}</p>}
                </label>
              );
            }
            if (field === "country") {
              return (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">{labels[field]}</span>
                  <input
                    type="text"
                    value="India"
                    disabled
                    className="w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none cursor-not-allowed"
                  />
                </label>
              );
            }
            if (field === "plan") {
              return (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                  <select
                    value={form.plan}
                    onChange={updateField("plan")}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#096dea] focus:bg-white"
                  >
                    <option value="starter">Starter</option>
                    <option value="growth">Growth</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </label>
              );
            }
            return (
              <label key={field} className="space-y-2" onFocus={() => touch(field)}>
                <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                <input
                  type="text"
                  value={form[field]}
                  onChange={updateField(field)}
                  className={fieldStyle(field)}
                />
                {errors[field] && <p className="text-xs text-rose-600">{errors[field]}</p>}
              </label>
            );
          })
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-2" onFocus={() => touch("password")}>
          <span className="text-sm font-medium text-slate-700">{renderLabel("password")}</span>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={updateField("password")}
              className={`w-full rounded-xl border px-4 py-3 pr-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
                errors.password ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:bg-white" : "border-slate-200 bg-slate-50 focus:border-[#096dea] focus:bg-white"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-rose-600">{errors.password}</p>}
          {form.password && !errors.password && (
            <div className="mt-1 space-y-1">
              {feedback.map((item) => (
                <p key={item.label} className={`text-xs ${item.ok ? "text-green-600" : "text-slate-400"}`}>
                  {item.ok ? "\u2713" : "\u2022"} {item.label}
                </p>
              ))}
            </div>
          )}
        </label>

        <label className="space-y-2" onFocus={() => touch("confirmPassword")}>
          <span className="text-sm font-medium text-slate-700">{renderLabel("confirmPassword")}</span>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={form.confirmPassword}
              onChange={updateField("confirmPassword")}
              className={`w-full rounded-xl border px-4 py-3 pr-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
                errors.confirmPassword ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:bg-white" : "border-slate-200 bg-slate-50 focus:border-[#096dea] focus:bg-white"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-rose-600">{errors.confirmPassword}</p>}
        </label>
      </div>

      <label className="space-y-2 block">
        <span className="text-sm font-medium text-slate-700">{renderLabel("metadata_json")}</span>
        <textarea
          rows={6}
          value={form.metadata_json}
          onChange={updateField("metadata_json")}
          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#096dea] focus:bg-white"
        />
      </label>

      {(serverError || errorMessage) ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {serverError || errorMessage}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          onClick={() => { setForm(initialForm); setTouched({}); setServerError(""); }}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          Reset
        </button>
        <button
          type="submit"
          disabled={loading}
          className="rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Creating..." : "Create tenant"}
        </button>
      </div>
    </form>
  );
};

export default TenantCreateForm;
